/**
 * Compute the Monday-anchored week (7 days) containing the given date.
 * Returns array of Date objects [Mon..Sun].
 */
export function weekDates(anchor) {
  const d = new Date(anchor);
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() - day);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(monday);
    x.setDate(monday.getDate() + i);
    return x;
  });
}

export const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const HOURS = Array.from({ length: 13 }, (_, i) => 8 + i); // 08..20

const timeToMin = (t) => {
  const [h, m] = (t || "0:0").split(":").map(Number);
  return h * 60 + m;
};

/** For a given day's events, compute {top, height} in pixels (60px per hour). */
export function eventGeometry(startTime, endTime, hourHeightPx = 60, startHour = 8) {
  const s = timeToMin(startTime) - startHour * 60;
  const e = timeToMin(endTime) - startHour * 60;
  const top = Math.max(0, (s / 60) * hourHeightPx);
  const height = Math.max(24, ((e - s) / 60) * hourHeightPx);
  return { top, height };
}

const dayLabels = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export function WeekGrid({ anchor, events, onSlotClick }) {
  const days = weekDates(anchor);
  const eventsByDay = days.map((d) => {
    const iso = isoDate(d);
    return events.filter((e) => e.date === iso);
  });

  return (
    <div className="dec-card p-3 overflow-x-auto" data-testid="week-grid">
      <div className="grid grid-cols-[60px_repeat(7,minmax(120px,1fr))] min-w-[820px]">
        <div />
        {days.map((d, i) => (
          <div key={i} className="text-center pb-2 border-b border-[#E6E1DA]">
            <div className="text-[11px] uppercase tracking-wider text-[#5C6561]">{dayLabels[i]}</div>
            <div className="text-lg font-semibold">{d.getDate()}</div>
          </div>
        ))}

        {HOURS.map((h, hi) => (
          <React.Fragment key={h}>
            <div className="text-[10px] text-[#5C6561] pt-1 pr-2 text-right">{String(h).padStart(2, "0")}:00</div>
            {days.map((d, di) => (
              <div
                key={di}
                onClick={() => onSlotClick && onSlotClick(isoDate(d), `${String(h).padStart(2, "0")}:00`)}
                className="relative border-b border-l border-[#EEE9E2] h-[60px] hover:bg-[#F4EFEA] cursor-pointer"
                data-testid={`week-slot-${isoDate(d)}-${h}`}
              >
                {hi === 0 && eventsByDay[di].map((e) => {
                  const g = eventGeometry(e.start_time, e.end_time);
                  return (
                    <div
                      key={e.id}
                      className="absolute left-1 right-1 rounded-md px-2 py-1 text-[11px] leading-tight bg-[#FDF0EC] border border-[#D95D39]/40 text-[#1F2421] overflow-hidden shadow-sm"
                      style={{ top: g.top, height: g.height, zIndex: 2 }}
                      data-testid={`week-event-${e.id}`}
                    >
                      <div className="font-semibold truncate">{e.title}</div>
                      <div className="text-[10px] text-[#5C6561]">{e.start_time}–{e.end_time}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

import React from "react";
export default WeekGrid;
